import type { AuthUser } from '@/types/api';

/**
 * Where each role belongs when it has not asked for anywhere in particular.
 *
 * ## Why this is one function and not four `<Navigate to="/dashboard">`
 *
 * Four places decide this: signing in, completing a 2FA challenge, hitting `/` with a session,
 * and hitting `/login` with a session. When the answer was the constant `/dashboard` that was
 * harmless duplication. It stops being harmless the moment the answer depends on who is asking —
 * three of the four get updated and the fourth quietly sends the operator to the wrong page,
 * which is exactly the bug this fixes.
 *
 * ## Why staff do not land on the customer dashboard
 *
 * `/dashboard` is scoped to the signed-in tenant. An operator has a tenant of their own, so the
 * page renders — it just shows *their own* servers, which is not what running a platform looks
 * like. The symptom is convincing: it draws real charts from real data, so nothing appears broken.
 * The operator's equivalent is `/admin`, which reports the machine the platform runs on and the
 * signals that mean the platform itself broke.
 *
 * Support is deliberately sent somewhere else again. Their work is a named customer with a
 * problem, and the platform host is not in their sidebar — landing them on a page their own
 * navigation does not offer would be its own small confusion.
 */

/** Every role that is platform staff rather than a customer. Mirrors `Actor::PLATFORM_ROLES`. */
export const STAFF_ROLES = ['super_admin', 'admin', 'support'];

/** Staff who run the platform, as opposed to staff who help its customers. */
const OPERATOR_ROLES = ['super_admin', 'admin'];

export function isStaff(role: string | undefined): boolean {
  return role !== undefined && STAFF_ROLES.includes(role);
}

export function landingPathFor(
  user: AuthUser | null,
  options: { impersonating?: boolean } = {},
): string {
  // An administrator viewing as a customer is *acting* as that customer, and their token is
  // scoped accordingly — `/admin` would be refused by the API it calls. The banner is the
  // reminder of who they really are; the destination follows the role they are wearing.
  if (options.impersonating === true) {
    return '/dashboard';
  }

  if (user !== null && OPERATOR_ROLES.includes(user.role)) {
    return '/admin';
  }

  if (user !== null && user.role === 'support') {
    return '/admin/customers';
  }

  return '/dashboard';
}
